"use client";

import IconEye from "@/assets/icons/eye.svg";
import InfoIcon from "@/assets/icons/info.svg?react";
import { TranslationData } from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import Tooltip from "@/components/ui/tooltip/Tooltip";
import { cn } from "@/lib/tailwind";
import { IPowerList } from "@/utils/product.utils";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import PowerSelector from "./PowerSelector";
import QuantitySelector from "./QuantitySelector";
import { getCountryCodeFromLocale } from "@/apiUtils/commonUtils";
import { useParams } from "next/navigation";

interface IProps {
  translations: TranslationData;
  powerList: Record<string, IPowerList[]>;
  selectedColor: string;
  handleVariantSelection: (id: string) => void;
  handleSecondaryVariantSelection: (id: string) => void;
  hasPowerSelectionError: boolean;
  hasPowerSelected: boolean;
  handlePowerSelectionStatus: (isSelected: boolean) => void;
  quantitySelectorProps: {
    handleQuantityChange: (quantity: number, isSecondaryVariant: boolean) => void;
    quantity: number;
    secondaryVariantQuantity: number;
  };
  handleResetPowerSelectionError: () => void;
  selectedPack?: string;
}

const PowerSelection = ({
  translations,
  powerList,
  selectedColor,
  handleVariantSelection,
  handleSecondaryVariantSelection,
  handlePowerSelectionStatus,
  hasPowerSelected,
  hasPowerSelectionError,
  quantitySelectorProps,
  selectedPack,
  handleResetPowerSelectionError,
}: IProps) => {
  const [multiPower, setMultiPower] = useState(false);
  const [selectedPower, setSelectedPower] = useState<string>();
  const [secondarySelectedPower, setSecondarySelectedPower] = useState<string>();
  const [tooltip, setTooltip] = useState(false);
  const { locale } = useParams();

  function handlePowerSelection(
    val: string | number,
    key: "leftEye" | "rightEye",
    handler: (id: string) => void,
  ) {
    if (key === "leftEye") {
      setSelectedPower(val?.toString());
    } else if (key === "rightEye") {
      setSecondarySelectedPower(val?.toString());
    }

    const item = powerList[selectedColor]?.find((v) => v.power === val);
    handler(item?.id!);
    handlePowerSelectionStatus(true);
    handleResetPowerSelectionError();
  }

  const quantity = useMemo(() => {
    const primary = powerList[selectedColor]?.find((v) => v.power === selectedPower)?.quantity;
    const secondary = powerList[selectedColor]?.find(
      (v) => v.power === secondarySelectedPower,
    )?.quantity;

    return { primary, secondary };
  }, [selectedColor, selectedPower, secondarySelectedPower]);

  // const { primary, secondary } = useMemo(() => {
  //   const primary = powerList[selectedColor]?.find((v) => v.power === selectedPower);
  //   const secondary = powerList[selectedColor]?.find((v) => v.power === secondarySelectedPower);
  //   return { primary, secondary };
  // }, [powerList, selectedColor, selectedPower, secondarySelectedPower]);

  const packSize = useMemo(() => {
    const match = powerList[selectedColor]?.find((v) => v.power === selectedPower);
    return match?.clPerPackSize;
  }, [selectedColor, selectedPower, powerList]);

  const secondaryPackSize = useMemo(() => {
    const match = powerList[selectedColor]?.find((v) => v.power === secondarySelectedPower);
    return match?.clPerPackSize;
  }, [selectedColor, secondarySelectedPower, powerList]);

  useEffect(() => {
    const isPowerSelected = multiPower
      ? !!(selectedPower && secondarySelectedPower)
      : !!selectedPower;

    if (isPowerSelected) {
      handlePowerSelectionStatus(true);
    } else {
      handlePowerSelectionStatus(false);
    }
  }, [selectedPower, multiPower, secondarySelectedPower]);

  useEffect(() => {
    quantitySelectorProps?.handleQuantityChange(1, false);
  }, [selectedPower]);

  useEffect(() => {
    quantitySelectorProps?.handleQuantityChange(1, true);
  }, [secondarySelectedPower]);

  return (
    <div id="power-prescription-panel">
      <div className="flex items-center gap-2 px-0 py-5 lg:px-6">
        <span className="text-base font-semibold">
          {translations?.pdp?.pdp_enter_your_prescription_label}
        </span>
        <span
          className="relative"
          role="button"
          onMouseEnter={() => setTooltip(true)}
          onMouseLeave={() => setTooltip(false)}
          onClick={() => setTooltip(!tooltip)}
        >
          <InfoIcon className="w-5" />
          <Tooltip
            display={tooltip}
            title={translations?.pdp?.pdp_prescription_overlay_title}
            description={translations?.pdp?.pdp_prescription_overlay_description ?? ""}
            // position="bottomRight"
            className="top-5 w-[18rem] lg:-top-6 lg:w-[20rem] ltr:left-6 max-sm:ltr:-left-[10rem]"
          />
        </span>
      </div>
      <div
        className={cn(
          "bg-transparent md:bg-tertiary-5",
          hasPowerSelectionError ? "border border-secondary-1 p-3" : "",
        )}
      >
        <div className="border-b py-5 md:px-7">
          {multiPower && (
            <div
              className={cn(
                "grid justify-items-end gap-4 pb-2 text-[0.8em] text-tertiary-6 md:gap-8",
                multiPower ? "grid-cols-3" : "grid-cols-2",
              )}
            >
              <span></span>
              <span className="w-[5.5rem] md:w-[6.5rem]">
                {translations?.lensConfigurator?.lens_config_left_eye_subtitle}
              </span>
              <span className="w-[5.5rem] md:w-[6.5rem]">
                {translations?.lensConfigurator?.lens_config_right_eye_subtitle}
              </span>
            </div>
          )}
          <div className={cn("grid gap-4 md:gap-8", multiPower ? "grid-cols-3" : "grid-cols-2")}>
            <div className="flex items-center text-sm">
              {translations?.common?.common_variant_attribute_power_label}
            </div>
            <PowerSelector
              translations={translations}
              powerList={powerList}
              selectedColor={selectedColor}
              handleOptionClick={(val) =>
                handlePowerSelection(val, "leftEye", handleVariantSelection)
              }
              value={selectedPower}
            />
            {multiPower && (
              <PowerSelector
                translations={translations}
                powerList={powerList}
                selectedColor={selectedColor}
                handleOptionClick={(val) =>
                  handlePowerSelection(val, "rightEye", handleSecondaryVariantSelection)
                }
                value={secondarySelectedPower}
              />
            )}
          </div>
          <div
            className={cn("mt-4 grid gap-4 md:gap-8", multiPower ? "grid-cols-3" : "grid-cols-2")}
          >
            <div className="mt-2 text-sm">{translations?.pdp?.pdp_no_of_boxes_input_label}</div>
            <QuantitySelector
              className={multiPower ? "[@media(max-width:370px)]:items-baseline" : ""}
              value={quantitySelectorProps?.quantity}
              max={quantity?.primary ?? 1}
              min={1}
              handleAdd={() =>
                quantitySelectorProps?.handleQuantityChange(++quantitySelectorProps.quantity, false)
              }
              handleSub={() =>
                quantitySelectorProps.handleQuantityChange(--quantitySelectorProps.quantity, false)
              }
              selectedPack={selectedPack ?? packSize}
              multipleLabel={translations?.pdp?.pdp_no_of_lens_count_per_box_multiple_label ?? ""}
              singleLabel={translations?.pdp?.pdp_no_of_lens_count_per_box_single_label ?? ""}
            />
            {multiPower && (
              <QuantitySelector
                className={multiPower ? "[@media(max-width:370px)]:items-baseline" : ""}
                value={quantitySelectorProps?.secondaryVariantQuantity}
                max={quantity?.secondary ?? 1}
                min={1}
                handleAdd={() =>
                  quantitySelectorProps.handleQuantityChange(
                    ++quantitySelectorProps.secondaryVariantQuantity,
                    true,
                  )
                }
                handleSub={() =>
                  quantitySelectorProps.handleQuantityChange(
                    --quantitySelectorProps.secondaryVariantQuantity,
                    true,
                  )
                }
                selectedPack={selectedPack ?? secondaryPackSize}
                multipleLabel={translations?.pdp?.pdp_no_of_lens_count_per_box_multiple_label ?? ""}
                singleLabel={translations?.pdp?.pdp_no_of_lens_count_per_box_single_label ?? ""}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 px-0 py-4 max-sm:border-b lg:px-6">
          <input
            type="checkbox"
            id="multiPowerCheckbox"
            className="cursor-pointer rounded-sm outline-none ring-tertiary-5 focus-within:ring-0 focus:ring-0"
            defaultChecked={multiPower}
            onClick={() => setMultiPower(!multiPower)}
          />
          <label className="cursor-pointer text-sm font-normal" htmlFor="multiPowerCheckbox">
            {translations?.pdp?.pdp_i_have_two_powers_checkbox_label}
          </label>
        </div>
      </div>

      {hasPowerSelectionError && (
        <div className="bg-[#FAE3DE]">
          <p className="p-3 text-sm text-secondary-1">
            {translations?.pdp?.pdp_select_power_alert_message}
          </p>
        </div>
      )}

      {translations?.pdp?.pdp_book_eye_test_link &&
        !["sa", "eg"].includes(getCountryCodeFromLocale(locale as string)) && (
          <div className="flex gap-4 pt-6 text-sm md:items-center md:px-5 [@media(max-width:370px)]:flex-col">
            <span className="text-[0.813rem] font-bold">
              {translations?.pdp?.pdp_dont_know_prescription_link}
            </span>
            <CLink href={`/${locale}/book-eyetest/type`} className="flex items-center gap-2 underline">
              <Image src={IconEye} alt="Book eye test icon" />
              <span className="text-[0.813rem] text-[#666666]">
                {translations?.pdp?.pdp_book_eye_test_link}
              </span>
            </CLink>
          </div>
        )}
    </div>
  );
};

export default PowerSelection;
